import React, { useState, useEffect, useRef } from "react";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { changeDateToYearMonthDay } from "../../../Utils/utils";

import CalendarIcon from "assets/medication/calender.svg";
import enGB from "date-fns/locale/en-GB";
import moment from "moment";

// Set locale to start the week on Monday
enGB.options = { ...enGB.options, weekStartsOn: 1 };

registerLocale("en-GB", enGB);

const DATE_FORMAT_HINT =
  "Please enter a valid date as DD/MM/YYYY (e.g. 24/09/2026).";
const DATE_PAST_HINT = "Expiry date must be a future date.";
const DATE_FUTURE_HINT = "Expiry date is beyond the allowed range.";
const MIN_VALID_YEAR = 1900;
const MAX_VALID_YEAR = 9999; // any 4-digit year; blocks nonsense like 129045

/** Keep only digits, soft-mask into DD/MM/YYYY while typing. */
const maskToDdMmYyyy = (raw = "") => {
  const digits = String(raw).replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const getDateValidationError = (date, minDate, maxDate) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return DATE_FORMAT_HINT;
  }
  const year = date.getFullYear();
  if (year < MIN_VALID_YEAR || year > MAX_VALID_YEAR) {
    return DATE_FORMAT_HINT;
  }
  if (
    minDate &&
    moment(date).startOf("day").isBefore(moment(minDate).startOf("day"))
  ) {
    return DATE_PAST_HINT;
  }
  if (
    maxDate &&
    moment(date).startOf("day").isAfter(moment(maxDate).endOf("day"))
  ) {
    return DATE_FUTURE_HINT;
  }
  return null;
};

const isReasonableCalendarDate = (date, minDate, maxDate) =>
  !getDateValidationError(date, minDate, maxDate);

const parseManualDate = (text, minDate, maxDate) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return { ok: false, empty: true };
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return { ok: false, empty: false, error: DATE_FORMAT_HINT };
  }
  const parsed = moment(trimmed, "DD/MM/YYYY", true);
  if (!parsed.isValid()) {
    return { ok: false, empty: false, error: DATE_FORMAT_HINT };
  }
  const asDate = parsed.toDate();
  const error = getDateValidationError(asDate, minDate, maxDate);
  if (error) {
    return { ok: false, empty: false, error };
  }
  return { ok: true, empty: false, date: asDate };
};

const DateRangePickerNew = (props) => {
  const {
    isSingleDate = false,
    singleSelectedDate,
    handleCancel,
    handleChange,
    fromDate,
    selected,
    toDate,
    isDisabled = false,
    showCalendar = false,
    isClearable = true,
    cssClass = "",
    maxDate,
    setSelectedFromDate = null,
    setIsDisableSearch,
    timesheet = false,
    showCalendarIcon = false,
    placeholder,
    minDate,
    isAllreadySelectedTimesheet = false,
    setCheckboxes,
    setPage,
    preventOpenOnFocus = false,
  } = props;
  const datePickerRef = useRef(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [dateInputError, setDateInputError] = useState("");
  const [manualText, setManualText] = useState("");
  const [isManualFocused, setIsManualFocused] = useState(false);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    setDateRange([
      fromDate ? new Date(fromDate) : null,
      toDate ? new Date(toDate) : null,
    ]);
  }, [fromDate, toDate]);

  useEffect(() => {
    if (isDisabled) {
      datePickerRef.current?.setOpen?.(false);
    }
  }, [isDisabled]);

  // Sync from parent only when user is not typing
  useEffect(() => {
    if (isManualFocused) return;
    if (
      singleSelectedDate &&
      isReasonableCalendarDate(singleSelectedDate, null, null)
    ) {
      setManualText(moment(singleSelectedDate).format("DD/MM/YYYY"));
      setDateInputError("");
    }
    // When parent clears after invalid input, keep manualText so the error stays visible
  }, [singleSelectedDate, isManualFocused]);

  const handleClickIcon = () => {
    if (isDisabled) return;
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  const applySingleDate = (date) => {
    if (!date) {
      setDateInputError("");
      setManualText("");
      handleCancel?.();
      return;
    }
    const error = getDateValidationError(date, minDate, maxDate);
    if (error) {
      setDateInputError(error);
      setManualText(moment(date).format("DD/MM/YYYY"));
      handleCancel?.();
      return;
    }
    setDateInputError("");
    setManualText(moment(date).format("DD/MM/YYYY"));
    handleChange?.(changeDateToYearMonthDay(date));
  };

  const commitManualText = (text) => {
    const result = parseManualDate(text, minDate, maxDate);
    if (result.empty) {
      setDateInputError("");
      handleCancel?.();
      return false;
    }
    if (!result.ok) {
      setDateInputError(result.error || DATE_FORMAT_HINT);
      // Keep what user typed so they can fix it; do not save invalid date
      handleCancel?.();
      return false;
    }
    setDateInputError("");
    setManualText(moment(result.date).format("DD/MM/YYYY"));
    handleChange?.(changeDateToYearMonthDay(result.date));
    return true;
  };

  if (isSingleDate) {
    const dateFromManualText = (() => {
      const text = (manualText || "").trim();
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return null;
      const parsed = moment(text, "DD/MM/YYYY", true);
      if (!parsed.isValid()) return null;
      const asDate = parsed.toDate();
      const year = asDate.getFullYear();
      if (year < MIN_VALID_YEAR || year > MAX_VALID_YEAR) return null;
      return asDate;
    })();

    // Prefer saved/selected expiry so edit opens on that month, not always today
    const calendarSelected =
      (singleSelectedDate &&
      !Number.isNaN(new Date(singleSelectedDate).getTime()) &&
      new Date(singleSelectedDate).getFullYear() >= MIN_VALID_YEAR &&
      new Date(singleSelectedDate).getFullYear() <= MAX_VALID_YEAR
        ? new Date(singleSelectedDate)
        : null) || dateFromManualText;

    const calendarOpenToDate = calendarSelected || minDate || new Date();

    return (
      <div
        className={`date-range-picker-field relative w-full min-w-0 ${
          isDisabled ? "pointer-events-none opacity-60" : ""
        }`}
      >
        <div className='relative w-full min-w-0'>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='off'
            maxLength={10}
            disabled={isDisabled}
            className={`did-floating-input w-full pr-9 ${cssClass} ${
              dateInputError ? "border-red-500" : ""
            }`}
            placeholder='DD/MM/YYYY'
            value={manualText}
            onFocus={() => setIsManualFocused(true)}
            onChange={(e) => {
              const next = maskToDdMmYyyy(e.target.value);
              setManualText(next);

              // Incomplete — allow typing, no error yet
              if (next.length < 10) {
                setDateInputError("");
                return;
              }

              // Full DD/MM/YYYY typed — validate; show error if invalid
              commitManualText(next);
            }}
            onBlur={() => {
              setIsManualFocused(false);
              const text = (manualText || "").trim();
              if (!text) {
                setDateInputError("");
                handleCancel?.();
                return;
              }
              // Incomplete or invalid on leave → show error, keep text
              commitManualText(text);
            }}
            onKeyDown={(e) => {
              if (e.ctrlKey || e.metaKey || e.altKey) return;
              const allowedKeys = [
                "Backspace",
                "Delete",
                "Tab",
                "Enter",
                "Escape",
                "ArrowLeft",
                "ArrowRight",
                "Home",
                "End",
              ];
              if (allowedKeys.includes(e.key)) return;
              if (e.key.length === 1 && !/[0-9/]/.test(e.key)) {
                e.preventDefault();
                setDateInputError(DATE_FORMAT_HINT);
              }
            }}
          />
          <img
            src={CalendarIcon}
            alt='calendar'
            className={`absolute right-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 ${
              isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            }`}
            onClick={handleClickIcon}
          />
          {/* Calendar popper only — manual typing uses the input above */}
          <div className='absolute left-0 top-full w-full'>
            <DatePicker
              ref={datePickerRef}
              dateFormat={"dd/MM/yyyy"}
              disabled={isDisabled}
              selectsRange={false}
              selected={
                calendarSelected &&
                isReasonableCalendarDate(calendarSelected, minDate, maxDate)
                  ? calendarSelected
                  : null
              }
              openToDate={calendarOpenToDate}
              onChange={(selectedDates) => {
                if (isDisabled) return;
                applySingleDate(selectedDates || null);
                setIsManualFocused(false);
              }}
              minDate={minDate}
              maxDate={maxDate}
              locale='en-GB'
              preventOpenOnFocus
              customInput={
                <button
                  type='button'
                  tabIndex={-1}
                  aria-hidden='true'
                  className='sr-only'
                />
              }
            />
          </div>
        </div>
        {dateInputError ? (
          <p className='text-xs text-red-500 mt-1'>{dateInputError}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`date-range-picker-field relative w-full min-w-0 ${
        isDisabled ? "pointer-events-none opacity-60" : ""
      }`}
    >
      <DatePicker
        ref={datePickerRef}
        inline={showCalendar}
        dateFormat={"dd/MM/yyyy"}
        disabled={isDisabled}
        wrapperClassName='w-full'
        className='did-floating-input capitalize w-full'
        selectsRange={true}
        openToDate={startDate || new Date()}
        selected={selected}
        startDate={startDate}
        endDate={endDate}
        onChange={(selectedDates) => {
          if (isDisabled) return;
          if (setSelectedFromDate && selectedDates[0]) {
            setSelectedFromDate(selectedDates[0]);
          } else if (setSelectedFromDate && !selectedDates[0]) {
            setSelectedFromDate(null);
          }
          if (
            selectedDates &&
            selectedDates.length > 0 &&
            !selectedDates[0] &&
            !selectedDates[1]
          ) {
            handleCancel();
            setDateRange([null, null]);
          } else if (
            selectedDates &&
            selectedDates.length > 0 &&
            selectedDates[0] &&
            selectedDates[1]
          ) {
            if (isAllreadySelectedTimesheet && setCheckboxes) {
              setCheckboxes([]);
            }
            setDateRange(selectedDates);
            if (setIsDisableSearch) {
              setIsDisableSearch(false);
            }
            if (setPage) {
              setPage(1);
            }
            handleChange({
              fromDate: changeDateToYearMonthDay(selectedDates[0]),
              toDate: changeDateToYearMonthDay(selectedDates[1]),
            });
          } else {
            setDateRange(selectedDates);
            if (setIsDisableSearch) {
              setIsDisableSearch(true);
            }
          }
        }}
        preventOpenOnFocus={preventOpenOnFocus}
        placeholderText={
          placeholder ? placeholder : "DD/MM/YYYY  to  DD/MM/YYYY "
        }
        isClearable={timesheet && !dateRange ? undefined : isClearable}
        maxDate={maxDate}
        locale='en-GB'
        popperClassName={showCalendarIcon && "custom-datepicker"}
      />
      {showCalendarIcon && !startDate && !endDate && (
        <img
          src={CalendarIcon}
          alt='calendar'
          className={`pointer-events-auto absolute right-3 top-1/2 z-[1] h-5 w-5 -translate-y-1/2 ${
            isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={handleClickIcon}
        />
      )}
      {timesheet && (
        <img
          src={CalendarIcon}
          alt='calendar'
          className={`pointer-events-auto absolute right-3 top-1/2 z-[1] h-5 w-5 -translate-y-1/2 ${
            isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
          onClick={handleClickIcon}
        />
      )}
    </div>
  );
};

export default DateRangePickerNew;
